import React from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  Check,
  Save,
  Rocket,
  Edit3,
  ChevronDown,
  Loader2,
  Clock,
} from 'lucide-react';
import type { ViewportMode } from '../types';
import type { PageRecord } from '../api';

export interface PreviewTopbarProps {
  viewport: ViewportMode;
  onViewportChange: (vp: ViewportMode) => void;
  pages: PageRecord[];
  activePageId: string;
  onSelectPage: (pageId: string) => void;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  onSaveDraft: () => void;
  onPublish: () => void;
  onEnableEditor: () => void;
}

export const PreviewTopbar: React.FC<PreviewTopbarProps> = ({
  viewport,
  onViewportChange,
  pages,
  activePageId,
  onSelectPage,
  isDirty,
  isSaving,
  lastSavedAt,
  onSaveDraft,
  onPublish,
  onEnableEditor,
}) => {
  const activePage = pages.find((p) => p.id === activePageId);

  return (
    <div className="h-9 shrink-0 bg-card border-b border-border px-3 flex items-center justify-between select-none z-50 text-foreground text-xs shadow-xs">
      {/* Left Section: Live Preview Badge + Page Switcher + Viewports */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Live Preview Indicator */}
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold text-[11px] border border-emerald-500/20">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>Live Preview</span>
        </div>

        {/* Page Switcher */}
        {pages.length > 0 && (
          <div className="relative flex items-center">
            <select
              value={activePageId}
              onChange={(e) => onSelectPage(e.target.value)}
              className="appearance-none bg-muted/60 hover:bg-muted border border-border/80 rounded px-2 pr-6 py-0.5 text-[11px] font-medium text-foreground cursor-pointer focus:outline-none focus:border-primary truncate max-w-[130px] sm:max-w-[180px]"
            >
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} ({p.route_path})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 h-3 w-3 text-muted-foreground pointer-events-none" />
          </div>
        )}

        {/* Viewport Modes */}
        <div className="hidden sm:flex items-center bg-muted/60 p-0.5 rounded border border-border/70">
          <button
            type="button"
            onClick={() => onViewportChange('desktop')}
            className={`p-1 rounded cursor-pointer transition-colors ${
              viewport === 'desktop' ? 'bg-background text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Desktop View (1200px)"
          >
            <Monitor className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onViewportChange('tablet')}
            className={`p-1 rounded cursor-pointer transition-colors ${
              viewport === 'tablet' ? 'bg-background text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Tablet View (768px)"
          >
            <Tablet className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onViewportChange('mobile')}
            className={`p-1 rounded cursor-pointer transition-colors ${
              viewport === 'mobile' ? 'bg-background text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Mobile View (375px)"
          >
            <Smartphone className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Center Section: Auto-Save / Save to Draft (Requirement 7) */}
      <div className="flex items-center gap-2">
        {isSaving ? (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin text-primary" />
            <span>Saving draft...</span>
          </div>
        ) : isDirty ? (
          <button
            type="button"
            onClick={onSaveDraft}
            className="flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 cursor-pointer transition-colors shadow-xs"
            title="Click to save changes now (Auto-saves every 5 mins)"
          >
            <Save className="h-3 w-3" />
            <span>Save to Draft</span>
          </button>
        ) : (
          <div
            className="flex items-center gap-1 text-[10px] text-muted-foreground"
            title={`Saved to draft. Periodic auto-save runs every 5 minutes.${
              lastSavedAt ? ` Last saved: ${lastSavedAt.toLocaleTimeString()}` : ''
            }`}
          >
            <Check className="h-3 w-3 text-emerald-500" />
            <span className="hidden sm:inline">Saved</span>
            <span className="text-[9px] opacity-70 flex items-center gap-0.5 font-mono">
              <Clock className="h-2.5 w-2.5" /> 5m auto
            </span>
          </div>
        )}
      </div>

      {/* Right Section: Publish & Enable Editor */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPublish}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] shadow-xs cursor-pointer transition-colors"
          title="Publish live to custom domain & CDN"
        >
          <Rocket className="h-3 w-3" />
          <span className="hidden sm:inline">Publish</span>
        </button>

        <button
          type="button"
          onClick={onEnableEditor}
          className="flex items-center gap-1 px-3 py-1 rounded bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-[11px] shadow-xs cursor-pointer transition-all hover:scale-[1.02]"
          title="Enable Editor Mode"
        >
          <Edit3 className="h-3 w-3" />
          <span>Enable Editor</span>
        </button>
      </div>
    </div>
  );
};
